import React, { useState } from 'react';
import Typography from '@material-ui/core/Typography';
import withStyles from '@material-ui/core/styles/withStyles';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import Button from '@material-ui/core/Button';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Page from 'Components/Page';
import { useForm, Controller } from 'react-hook-form';
import NumberFormat from 'react-number-format';
import * as actions from 'Store/actions';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import history from '../../history';
import { toBase64 } from '../../helpers';
import FormTextField from 'Components/FormTextField';
import FileUpload from 'Components/Dropzone/FileUpload';
import Dropzone from 'Components/Dropzone';
import Loading from 'Components/Loading';
import uuid from 'uuid';
import * as Regex from '../../regex';

const pageStyles = theme => ({
  pageWrapper: {
    paddingLeft: '3%',
    paddingRight: '3%',
  },
  addEditForm: {
    display: 'flex',
    flexDirection: 'column',
  },
  optionalMessage: {
    fontSize: 14,
  },
  button: {
    alignSelf: 'flex-start',
    marginTop: '90px',
  },
  photoContainer: {
    marginBottom: '40px',
  },
});

const tokenTypes = {
  fungible: 'Fungible Token',
  nonFungible: 'Non Fungible Token',
};

function Create({ classes, saveImagesLocally, path }) {
  const { register, handleSubmit, errors, control } = useForm();
  const [loading, setLoading] = useState(false);
  const [assetTypeForMessage, setAssetTypeForMessage] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const onSubmit = async data => {
    setLoading(true);
    console.log('data in onSubmit', data);
  };

  async function cropImages(images, setWidth) {
    // inspired from here: https://zocada.com/compress-resize-images-javascript-browser/
    let promisesArr = [];
    let croppedImageBlobs = [];

    images.forEach(localImage => {
      promisesArr.push(
        new Promise((resolve, reject) => {
          const fileName = uuid.v4();
          var url = URL.createObjectURL(localImage);

          const img = new Image();
          img.src = url;
          return (img.onload = () => {
            const elem = document.createElement('canvas');
            const ctx = elem.getContext('2d');
            const width = setWidth;
            const scaleFactor = width / img.width;
            elem.width = width;
            elem.height = img.height * scaleFactor;
            ctx.drawImage(img, 0, 0, width, img.height * scaleFactor);
            ctx.canvas.toBlob(
              blob => {
                const file = new File([blob], fileName, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                croppedImageBlobs.push(file);
                resolve(file);
              },
              'image/jpeg',
              0.95,
            );
          });
        }),
      );
    });

    return Promise.all(promisesArr)
      .then(res => {
        return croppedImageBlobs;
      })
      .catch(err => {
        console.warn('Error resizing images on page: ', err);
      });
  }

  function getAssetTypeHelperText() {
    if (errors.assetType && errors.assetType.message) {
      return errors.assetType.message;
    }

    if (!assetTypeForMessage) {
      return null;
    }

    if (assetTypeForMessage === tokenTypes.fungible) {
      return 'Fungible tokens represent things that are subdivided or owned by multiple people like tickets and coupons.';
    }

    return `NFT's (Non fungible tokens) represent things that are wholly owned by one person like art or merchandise.`;
  }

  const assetTypeHelperText = getAssetTypeHelperText();

  return (
    <Page wrapperStyles={classes.pageWrapper}>
      <>
        <Typography variant="h1">Create Token</Typography>
        <FormHelperText>Fungible and non-fungible tokens</FormHelperText>
        <form onSubmit={handleSubmit(onSubmit)} className={classes.addEditForm}>
          <FormTextField
            fullWidth
            label="Token Name"
            name="tokenName"
            required
            error={!!errors.tokenName}
            helperText={errors.tokenName && errors.tokenName.message}
            inputRef={register({ required: 'Token name is required' })}
            placeholder="Enter token name"
          />

          <FormControl className={classes.photoContainer}>
            <InputLabel
              style={{
                position: 'relative',
                marginTop: '50px',
                top: '-35px',
                marginBottom: '10px',
              }}
            >
              Photos{' '}
              <span className={classes.optionalMessage}>
                (optional) <span className={classes.maxNumMessage}> - Maximum number of images: 5</span>
              </span>
            </InputLabel>

            <FileUpload
              maxNumFiles={5}
              onChange={async images => {
                console.log(`images`, images);

                const resizedMainImages = await cropImages(images, 1000);
                const resizedThumbImages = await cropImages(images, 300);

                setUploadedFiles({});

                // saveImagesLocally({
                //   main: imagesBase64,
                //   thumbs: imagesBase64Thumbs,
                // });
              }}
            />
          </FormControl>

          <FormControl>
            <InputLabel error={!!errors.assetType}>What type of asset is this? *</InputLabel>
            <Controller
              control={control}
              rules={{ required: 'Token type is required' }}
              name="assetType"
              onChange={([event]) => {
                setAssetTypeForMessage(event.target.value);
                return event;
              }}
              as={
                <Select>
                  <MenuItem value={tokenTypes.nonFungible}>NFT (Non Fungible Token)</MenuItem>
                  <MenuItem value={tokenTypes.fungible}>Fungible Tokens (Tickets or Coupons)</MenuItem>
                </Select>
              }
            />
            {assetTypeHelperText && <FormHelperText error={!!errors.assetType}>{assetTypeHelperText}</FormHelperText>}
          </FormControl>

          <FormTextField
            fullWidth
            label="Description"
            name="description"
            inputRef={register()}
            multiline
            placeholder="Enter description"
          />

          <FormTextField
            fullWidth
            label="Creator Name"
            name="creatorName"
            inputRef={register()}
            placeholder="Enter creator name"
            helperText="Note: Your wallet address will always be the creator address. The creator named specified here is just accompanying information. Consistently publishing your tokens with the same account lets people know who you are."
          />

          {assetTypeForMessage === tokenTypes.fungible && (
            <FormTextField
              fullWidth
              label="Fungible Quantity"
              name="fungibleQuantity"
              required
              error={!!errors.fungibleQuantity}
              helperText={errors.fungibleQuantity && errors.fungibleQuantity.message}
              inputRef={register({ required: 'Fungible quantity is required' })}
              placeholder="Enter fungible quantity"
            />
          )}

          <Button type="submit" variant="contained" color="primary" className={classes.button}>
            Create Token
          </Button>
        </form>
      </>
    </Page>
  );
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      saveImagesLocally: actions.saveImagesLocally,
    },
    dispatch,
  );
}

function mapStateToProps({ itemDraft }, ownProps) {
  return {
    path: ownProps.location.pathname,
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withStyles(pageStyles)(Create)));
